import { action, observable } from "mobx";
import { LocalPatientService } from "application/patient/services";
import { PatientService } from "application/patient/services";
import { LookupService } from "common/services";
import {
    FacilityOrderService,
    FacilityOrderTemplateService,
    FacilityInventoryCalculationService,
} from "application/facility-order/services";
import { PickTicketHeaderService } from "application/pick-ticket/services";
import { FacilityOrderTemplateItemEditViewStore } from "application/facility-order/stores";
import { BaseViewStore } from "core/stores";
import { ModalParams } from "core/models";
import { applicationContext } from "core/utils";
@applicationContext
class FacilityOrderTemplateEditViewStore extends BaseViewStore {
    @observable accessor facilityOrderTemplateStores = null;
    @observable accessor btPatient = null;
    @observable accessor isImportEnabled = false;
    @observable accessor showButtonViewAllTemplates = false;
    @observable accessor isPickTicketHeaderInProgres = false;
    @observable accessor isPickTicketHeaderCompleted = false;

    constructor(rootStore) {
        super(rootStore);

        this.facilityOrderTemplateService = new FacilityOrderTemplateService(
            rootStore.application.baasic.apiClient
        );
        this.localPatientService = new LocalPatientService(
            rootStore.application.baasic.apiClient
        );
        this.patientService = new PatientService(
            rootStore.application.baasic.apiClient
        );
        this.lookupService = new LookupService(
            rootStore.application.baasic.apiClient
        );
        this.facilityOrderService = new FacilityOrderService(
            rootStore.application.baasic.apiClient
        );
        this.pickTicketHeaderService = new PickTicketHeaderService(
            rootStore.application.baasic.apiClient
        );
        this.facilityInventoryCalculationService =
            new FacilityInventoryCalculationService(
                rootStore.application.baasic.apiClient
            );

        const params = rootStore.routerStore.routerState.params;
        this.rootStore = rootStore;
        this.brightreeBaseUrl =
            rootStore.applicationStore.app.baasic.settings.brightreeBaseUrl;
        this.patientId = params.patientId;
        this.patientKey = params.patientKey;
        this.facilityOrderId = params.facilityOrderId;
        this.itemCategoryIds = params.itemCategoryIds;
        this.inActive = params.inActive;
        this.nonCurrent = params.nonCurrent;

        this.createFacilityOrderTemplateModal = new ModalParams({});
        this.editFacilityOrderTemplateInactiveReasonModal = new ModalParams({});
        this.patientInsuranceEditModal = new ModalParams({});
        this.patientEditModal = new ModalParams({});
        this.orderingDoctorEditModal = new ModalParams({});

        this.initalize(
            this.facilityOrderId,
            this.patientId,
            this.patientKey,
            this.itemCategoryIds,
            this.inActive,
            this.nonCurrent
        );
    }

    @action.bound
    async onViewUpdate() {
        const params = this.rootStore.routerStore.routerState.params;
        const {
            facilityOrderId,
            patientId,
            patientKey,
            itemCategoryIds,
            inActive,
            nonCurrent,
        } = params;

        await this.initalize(
            facilityOrderId,
            patientId,
            patientKey,
            itemCategoryIds,
            inActive,
            nonCurrent
        );
    }

    @action.bound
    async initalize(
        facilityOrderId,
        patientId,
        patientKey,
        itemCategoryIds,
        inActive,
        nonCurrent
    ) {
        try {
            this.loaderStore.suspend();
            this.contentLoading = true;

            this.facilityOrderId = facilityOrderId;
            this.patientId = patientId;
            this.patientKey = patientKey;
            this.itemCategoryIds = itemCategoryIds;
            this.inActive = inActive;
            this.nonCurrent = nonCurrent;
            await this.initializePatient(patientKey);
            await this.initializeTemplateNavigation(
                facilityOrderId,
                patientId,
                itemCategoryIds,
                inActive,
                nonCurrent
            );
            await this.initializeImportActionLookups();

            this.facilityInventoryCalculation =
                await this.facilityInventoryCalculationService.getExisting(
                    this.facilityOrderId
                );

            await this.initializeFacilityOrderTemplateList(
                facilityOrderId,
                patientId
            );
            await this.initializePickTicketHeader(facilityOrderId);

            this.showButtonViewAllTemplates =
                (this.itemCategoryIds && this.itemCategoryIds.length > 0) ||
                this.inActive === false ||
                this.nonCurrent === false;

            this.loaderStore.resume();
            this.contentLoading = false;
        } catch (err) {
            this.loaderStore.resume();
            this.contentLoading = false;

            this.rootStore.notificationStore.error(
                "An unexpected error occurred while trying to fetch Patient and Templates",
                err
            );
        }
    }

    @action.bound
    async initializePatient(patientKey) {
        const patientResponse = await this.patientService.get(patientKey);
        this.btPatient = {
            patientId:
                patientResponse.data.patientGeneralInfo &&
                patientResponse.data.patientGeneralInfo.ptID,
            firstName:
                patientResponse.data.patientGeneralInfo &&
                patientResponse.data.patientGeneralInfo.name.first,
            lastName:
                patientResponse.data.patientGeneralInfo &&
                patientResponse.data.patientGeneralInfo.name.last,
            patientKey: patientResponse.data.brightreeID,
            dateOfBirth:
                patientResponse.data.patientGeneralInfo &&
                patientResponse.data.patientGeneralInfo.birthDate,
            ssn:
                patientResponse.data.patientGeneralInfo &&
                patientResponse.data.patientGeneralInfo.ssn,
            height:
                patientResponse.data.patientClinicalInfo &&
                patientResponse.data.patientClinicalInfo.height,
            weight:
                patientResponse.data.patientClinicalInfo &&
                patientResponse.data.patientClinicalInfo.weight,
            gender:
                patientResponse.data.patientClinicalInfo &&
                patientResponse.data.patientClinicalInfo.gender, // && (patientResponse.data.patientClinicalInfo.gender === "Male" || patientResponse.data.PatientClinicalInfo.gender === "Female") ? patientResponse.data.PatientClinicalInfo.gender : null,
            orderingDoctor:
                patientResponse.data.patientClinicalInfo &&
                patientResponse.data.patientClinicalInfo.orderingDoctor &&
                patientResponse.data.patientClinicalInfo.orderingDoctor.doctor
                    .value,
            orderingDoctorId:
                patientResponse.data.patientClinicalInfo &&
                patientResponse.data.patientClinicalInfo.orderingDoctor &&
                patientResponse.data.patientClinicalInfo.orderingDoctor.doctor
                    .id,
            activePrimaryPayor: patientResponse.data.primaryActivePayor,
            activeSecondaryPayor: patientResponse.data.secondaryActivePayor,
        };
        this.rootStore.titleStore.setTitle(
            this.btPatient
                ? `PT - ${this.btPatient.lastName}, ${this.btPatient.firstName}`
                : "Patient Templates"
        );
    }

    @action.bound
    async validatePatientHeightAndWeight(facilityOrderId, patientId) {
        await this.facilityOrderTemplateService.validatePatientHeightAndWeight(
            facilityOrderId,
            patientId
        );
        await this.initializeFacilityOrderTemplateList(
            facilityOrderId,
            patientId
        );
    }

    @action.bound
    async initializeTemplateNavigation(facilityOrderId, patientId, itemCategoryIds, inActive, nonCurrent) {
        const response =
            await this.facilityOrderTemplateService.getTemplateNavigation(
                facilityOrderId,
                patientId,
                itemCategoryIds && itemCategoryIds.length === 0
                    ? null
                    : itemCategoryIds,
                inActive,
                nonCurrent
            );

        this.previousPatient = response.data.previousPatient;
        this.nextPatient = response.data.nextPatient;
    }

    @action.bound
    async initializeImportActionLookups() {
        const response = await this.lookupService.getLookups("ImportAction");
        this.importActions = response.importAction;
    }

    @action.bound
    async initializeFacilityOrderTemplateList(facilityOrderId, patientId, showAllTemplates = false) {
        if (this.itemCategoryIds && this.itemCategoryIds.length == 0) {
            this.itemCategoryIds = null;
        }
        const params = {
            sort: "dateCreated|desc",
            facilityOrderId: facilityOrderId,
            patientId: patientId,
            fields: "id,isReviewed,importDetailStatus,importDetailStatus.abrv",
            embed: "ImportDetailStatus",
            inActive: showAllTemplates ? null : this.inActive,
            nonCurrent: showAllTemplates ? null : this.nonCurrent,
            itemCategoryIds: showAllTemplates ? null : this.itemCategoryIds,
        };

        const response = await this.facilityOrderTemplateService.find(params);
        const ids = response.data.item.map((x) => x.id);

        if (showAllTemplates) {
            this.showButtonViewAllTemplates = false;
        }

        this.facilityOrderTemplateStores = ids.map(
            (id) =>
                new FacilityOrderTemplateItemEditViewStore(this.rootStore, {
                    id: id,
                    insuranceInfo: this.btPatient.activePrimaryPayor,
                    refreshTemplates: this.initializeFacilityOrderTemplateList,
                    removeFromFacilityOrderTemplateList:
                        this.removeFromFacilityOrderTemplateList,
                    isReadyForImportFacilityOrder:
                        this.isReadyForImportFacilityOrder,
                    saveAllTemplates: () => this.saveAll(),
                    btPatient: this.btPatient,
                    facilityInventoryCalculation:
                        this.facilityInventoryCalculation,
                })
        );

        this.isImportEnabled = response.data.item.some(
            (i) => i.isReviewed === true && i.importDetailStatus.abrv === "new"
        );
    }

    @action.bound
    async initializePickTicketHeader(facilityOrderId) {
        const response = await this.facilityOrderService.get(facilityOrderId);
        if (response.data.pickTicketHeaderId !== null) {
            const pickTicketHeaderResponse =
                await this.pickTicketHeaderService.get(
                    response.data.pickTicketHeaderId,
                    { embed: "pickTicketHeaderStatus" }
                );
            this.isPickTicketHeaderInProgres =
                pickTicketHeaderResponse &&
                pickTicketHeaderResponse.data &&
                pickTicketHeaderResponse.data.pickTicketHeaderStatus &&
                pickTicketHeaderResponse.data.pickTicketHeaderStatus.abrv ===
                    "in-progress";
            this.isPickTicketHeaderCompleted =
                pickTicketHeaderResponse &&
                pickTicketHeaderResponse.data &&
                pickTicketHeaderResponse.data.pickTicketHeaderStatus &&
                pickTicketHeaderResponse.data.pickTicketHeaderStatus.abrv ===
                    "completed";
        }
    }

    @action.bound
    goToPatientPreview(patient) {
        let url = this.rootStore.routerStore.findRoute(
            "master.app.main.patient.preview.general"
        ).pattern;
        url = url
            .replace(":id", patient.patientKey)
            .replace(":firstName", patient.firstName)
            .replace(":lastName", patient.lastName);
        window.open(`${url}`);
    }

    @action.bound
    async goToNextPatient(saveTemplate = true) {
        if (!this.nextPatient) return;

        if (saveTemplate) {
            var flag = false;
            this.facilityOrderTemplateStores &&
                this.facilityOrderTemplateStores.forEach(async (s) => {
                    if (s.form.isDirty || s.isDirty) {
                        flag = true;
                        await s.saveTemplateAndItems(false);
                    }
                });
            if (flag) {
                await setTimeout(
                    () =>
                        this.rootStore.notificationStore.success(
                            "Successfully updated facility order templates and items."
                        ),
                    10
                );
            }
        }

        this.rootStore.routerStore.goTo(
            "master.app.main.facility-order.facility-order-template.edit",
            {
                patientId: this.nextPatient.id,
                patientKey: this.nextPatient.patientKey,
                facilityOrderId: this.facilityOrderId,
                inActive: this.inActive,
                nonCurrent: this.nonCurrent,
                itemCategoryIds: this.itemCategoryIds,
            },
            null,
            {
                forcePageRerender: true,
            }
        );
    }

    @action.bound
    async goToPreviousPatient() {
        if (!this.previousPatient) return;

        var flag = false;
        this.facilityOrderTemplateStores &&
            this.facilityOrderTemplateStores.forEach(async (s) => {
                if (s.form.isDirty || s.isDirty) {
                    flag = true;
                    await s.saveTemplateAndItems(false);
                }
            });

        if (flag) {
            await setTimeout(
                () =>
                    this.rootStore.notificationStore.success(
                        "Successfully updated facility order templates and items."
                    ),
                10
            );
        }

        this.rootStore.routerStore.goTo(
            "master.app.main.facility-order.facility-order-template.edit",
            {
                patientId: this.previousPatient.id,
                patientKey: this.previousPatient.patientKey,
                facilityOrderId: this.facilityOrderId,
                itemCategoryIds: this.itemCategoryIds,
                inActive: this.inActive,
                nonCurrent: this.nonCurrent,
            },
            null,
            {
                forcePageRerender: true,
            }
        );
    }

    @action.bound
    isNewTemplate(template) {
        if (template) {
            return template.templateId === null;
        }
    }

    @action.bound
    viewAllTemplates() {
        this.initializeFacilityOrderTemplateList(
            this.facilityOrderId,
            this.patientId,
            true
        );
    }

    @action.bound
    openCreateFacilityOrderTemplateModal() {
        this.createFacilityOrderTemplateModal.open({
            patientId: this.patientId,
            facilityOrderId: this.facilityOrderId,
            saveAllFacilityOrderTemplates: () => this.saveAll(),
            setFacilityOrderTemplateList: () =>
                this.initializeFacilityOrderTemplateList(
                    this.facilityOrderId,
                    this.patientId
                ),
        });
    }

    @action.bound
    openNewPatientPage() {
        this.rootStore.routerStore.goTo(
            "master.app.main.facility-order.facility-order-template.create-patient",
            {
                facilityOrderId: this.facilityOrderId,
            }
        );
    }

    @action.bound
    openEditFacilityOrderTemplateInactiveReasonModal() {
        this.editFacilityOrderTemplateInactiveReasonModal.open({
            patientId: this.patientId,
            facilityOrderId: this.facilityOrderId,
            setFacilityOrderTemplateList: () =>
                this.initializeFacilityOrderTemplateList(
                    this.facilityOrderId,
                    this.patientId
                ),
            checkAreTemplatesLinkedWithSpecificAreaOfCare: () =>
                this.checkAreTemplatesLinkedWithSpecificAreaOfCare(),
        });
    }

    @action.bound
    async checkAreTemplatesLinkedWithSpecificAreaOfCare() {
        var response =
            await this.facilityOrderTemplateService.isTemplateCategoryLinkedWithDoNotSupplyAreaOfCareOnFacility(
                {
                    facilityOrderId: this.facilityOrderId,
                    patientId: this.patientId,
                }
            );
        if (response.data) {
            this.rootStore.notificationStore.error(
                "Some templates are linked with an Area of Care whose Coverage Type on the Facility is Don't Supply."
            );
        }
    }

    @action.bound
    async deleteNewTemplate(facilityOrderTemplateId, linkedTemplateId = null) {
        try {
            await this.facilityOrderTemplateService.deleteNewTemplate(
                facilityOrderTemplateId
            );

            if (linkedTemplateId) {
                this.facilityOrderTemplateStores =
                    this.facilityOrderTemplateStores.filter(
                        (s) =>
                            s.item.id !== facilityOrderTemplateId &&
                            s.item.id !== linkedTemplateId
                    );
            } else {
                let store = this.facilityOrderTemplateStores.find(
                    (s) => s.item.linkedTemplateId === facilityOrderTemplateId
                );
                if (store) {
                    store.item.linkedTemplateId = null;
                }
                this.facilityOrderTemplateStores =
                    this.facilityOrderTemplateStores.filter(
                        (s) => s.item.id !== facilityOrderTemplateId
                    );
            }

            this.isReadyForImportFacilityOrder();

            this.rootStore.notificationStore.success(
                "Facility order template successfully deleted."
            );
        } catch (err) {
            this.rootStore.notificationStore.error(
                "Error occurred while trying to delete a template."
            );
        }
    }

    @action.bound
    removeFromFacilityOrderTemplateList(facilityOrderTemplateId) {
        if (facilityOrderTemplateId) {
            this.facilityOrderTemplateStores =
                this.facilityOrderTemplateStores.filter(
                    (s) => s.item.id !== facilityOrderTemplateId
                );
            let store = this.facilityOrderTemplateStores.find(
                (s) => s.item.linkedTemplateId === facilityOrderTemplateId
            );
            if (store) {
                store.item.linkedTemplateId = null;
            }
        }
    }

    @action.bound
    async isReadyForImportFacilityOrder() {
        var result =
            await this.facilityOrderService.isReadyForImportFacilityOrder({
                facilityOrderId: this.facilityOrderId,
                patientId: this.patientId,
            });
        this.isImportEnabled = result.data;
    }

    isAnyFacilityOrderTemplateFormInvalid(facilityOrderTemplateStores) {
        if (facilityOrderTemplateStores) {
            return this.facilityOrderTemplateStores.some(
                (s) => s.form.isValid === false
            );
        }

        return false;
    }

    @action.bound
    async goBackToPatientList() {
        await this.saveAllPatientTemplates();
        let url = this.rootStore.routerStore
            .findRoute(
                "master.app.main.facility-order.facility-order-template.list"
            )
            .pattern.replace(":id", this.facilityOrderId);
        window.location.replace(`${url}`);
    }

    @action.bound
    openPatientInsuranceEditModal() {
        const patientKey = this.btPatient.patientKey;

        this.patientInsuranceEditModal.open({
            patientKey: patientKey,
            refreshPatientData: this.initializePatient,
            refreshTemplatesData: () =>
                this.initializeFacilityOrderTemplateList(
                    this.facilityOrderId,
                    this.patientId
                ),
        });
    }

    @action.bound
    async importFacilityOrder() {
        var response =
            await this.facilityOrderTemplateService.isReviewedFacilityOrderAsync(
                { facilityOrderId: this.facilityOrderId }
            );
        this.rootStore.modalStore.showConfirm(
            response.data
                ? "FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_IMPORT"
                : "FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_NOT_REVIEWED",
            async () => {
                try {
                    await this.facilityOrderService.importFacilityOrder({
                        facilityOrderId: this.facilityOrderId,
                        patientId: this.patientId,
                    });
                    this.rootStore.notificationStore.success(
                        "Facility order templates imported successfully."
                    );
                } catch (err) {
                    if (err.data && err.data.errors) {
                        this.rootStore.notificationStore.error(err.data.errors);
                    } else {
                        this.rootStore.notificationStore.error(
                            "Error occurred while trying to import a facility order templates."
                        );
                    }
                }
                await this.initializeFacilityOrderTemplateList(
                    this.facilityOrderId,
                    this.patientId
                );
            }
        );
    }

    @action.bound
    openPatientEditModal() {
        const patientKey = this.btPatient.patientKey;

        this.patientEditModal.open({
            patientKey: patientKey,
            refreshPatientData: this.initializePatient,
            validatePatientHeightAndWeight: () =>
                this.validatePatientHeightAndWeight(
                    this.facilityOrderId,
                    this.patientId
                ),
        });
    }

    @action.bound
    openOrderingDoctorEditModal() {
        const patientKey = this.btPatient.patientKey;
        const orderingDoctorId = this.btPatient.orderingDoctorId;
        const orderingDoctor = this.btPatient.orderingDoctor;
        this.orderingDoctorEditModal.open({
            patientKey: patientKey,
            refreshPatientData: this.initializePatient,
            orderingDoctorId: orderingDoctorId,
            orderingDoctor: orderingDoctor,
        });
    }

    @action.bound
    async reviewAll() {
        if (
            this.isAnyFacilityOrderTemplateFormInvalid(
                this.facilityOrderTemplateStores
            )
        ) {
            this.rootStore.notificationStore.warning(
                "Please enter all required fields."
            );
            return;
        }

        try {
            this.facilityOrderTemplateStores &&
                this.facilityOrderTemplateStores.forEach(async (s) => {
                    if (
                        s.facilityOrderTemplateItems.length !== 0 &&
                        s.facilityOrderTemplateItems[0].id !== null
                    ) {
                        await s.saveTemplateAndItems(true, true);
                    }
                });

            await this.goToNextPatient(false);
        } catch (err) {
            this.rootStore.notificationStore.error(
                "Error occurred while trying to update facility order templates and items."
            );
        }
    }

    @action.bound
    async saveAllPatientTemplates() {
        try {
            this.facilityOrderTemplateStores &&
                this.facilityOrderTemplateStores.forEach(async (s) => {
                    if (
                        s.facilityOrderTemplateItems.length !== 0 &&
                        s.facilityOrderTemplateItems[0].id !== null
                    ) {
                        await s.saveTemplateAndItems(false, false);
                    }
                });
        } catch (err) {
            this.rootStore.notificationStore.error(
                "Error occurred while trying to update facility order templates and items."
            );
        }
    }

    @action.bound
    async saveAll() {
        try {
            this.facilityOrderTemplateStores &&
                this.facilityOrderTemplateStores.forEach(async (s) => {
                    if (
                        s.facilityOrderTemplateItems.length !== 0 &&
                        s.facilityOrderTemplateItems[0].id !== null
                    ) {
                        await s.saveTemplateAndItems(false, false);
                    }
                });
        } catch (err) {
            this.rootStore.notificationStore.error(
                "Error occurred while trying to update facility order templates and items."
            );
        }
    }
}

export default FacilityOrderTemplateEditViewStore;
